import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const inputVariants = cva(
  [
    // Base styles
    "flex w-full rounded bg-background transition-all duration-200 ease-in-out",
    "text-foreground file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground",
    "placeholder:text-muted-foreground/70",
    
    // Focus states (now using CSS pseudo-classes)
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
    "focus:border-primary focus:shadow-sm focus:shadow-primary/10",
    
    // Hover states
    "hover:border-border/80 hover:shadow-sm",
    
    // Disabled states
    "disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-muted/30",
    
    // Error states (can be overridden with className)
    "aria-[invalid=true]:border-destructive aria-[invalid=true]:ring-destructive/20",
    // Motion/touch feedback (kept generic)
    "touch-feedback",
  ],
  {
    variants: {
      variant: {
        default: "border border-input",
        outline: "border-2 border-input",
        ghost: "border-0 bg-transparent hover:bg-accent hover:text-accent-foreground",
        underline: "border-0 border-b-2 border-input rounded-none bg-transparent px-0 pb-2 focus:border-primary",
        filled: "border-0 bg-muted/50 hover:bg-muted/70 focus:bg-background",
        solid: "border-2 border-primary bg-primary/5",
      },
      size: {
        // Mobile uses 16px font to prevent iOS Safari zoom-on-focus
        // (Safari zooms any input whose computed font-size is <16px).
        // Desktop reverts to the intended 12/14px density at the sm: breakpoint.
        xs: "h-8 px-2 text-[16px] sm:text-xs",
        sm: "h-9 px-2.5 text-[16px] sm:text-sm",
        md: "h-10 px-3 text-[16px] sm:text-sm",
        lg: "h-12 px-4 text-base",
        xl: "h-14 px-5 text-base",
      },
      radius: {
        none: "rounded-none",
        sm: "rounded-sm",
        md: "rounded-md",
        lg: "rounded-lg",
        full: "rounded-full",
      },
      state: {
        none: "",
        error:
          "border-destructive focus:border-destructive focus-visible:ring-destructive/30",
        success:
          "border-emerald-500/60 focus:border-emerald-500 focus-visible:ring-emerald-500/20",
        warning:
          "border-amber-500/60 focus:border-amber-500 focus-visible:ring-amber-500/20",
      },
      leadingIcon: {
        true: "pl-10",
      },
      trailingIcon: {
        true: "pr-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
      radius: "lg",
      state: "none",
    },
  }
)

export interface InputProps
  extends Omit<React.ComponentProps<"input">, "size">,
    VariantProps<typeof inputVariants> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, variant, size, radius, state, leadingIcon, trailingIcon, ...props }, ref) => {
    const internalRef = React.useRef<HTMLInputElement | null>(null);

    const mergedRef = React.useCallback((node: HTMLInputElement | null) => {
      internalRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    }, [ref]);

    // Block wheel-to-change on number inputs by briefly making it readOnly
    // during the wheel event — allows page scroll to pass through normally
    React.useEffect(() => {
      const el = internalRef.current;
      if (!el || type !== 'number') return;
      const handler = () => {
        el.readOnly = true;
        requestAnimationFrame(() => { el.readOnly = false; });
      };
      el.addEventListener('wheel', handler, { passive: true });
      return () => el.removeEventListener('wheel', handler);
    }, [type]);

    return (
      <input
        type={type}
        className={cn(
          inputVariants({ variant, size, radius, state, leadingIcon, trailingIcon }),
          className
        )}
        ref={mergedRef}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

// eslint-disable-next-line react-refresh/only-export-components
export { Input, inputVariants }
